import React, {
  useState, useContext, useEffect, useMemo, useRef
} from 'react';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { FLAGS } from '../platform/config/flags';
import { isCustomerTypeAllowed, isSurfaceEnabled } from '../platform/config/surface';
import { emit, subscribe } from '../platform/events/lifecycle';
import { AssistantActionButton } from '../shared/fab/AssistantActionButton';
import { LazyAssistantDataProvider } from './dynamic-import';
import { LazyAssistantShell, loadAssistantShell } from '../shell/dynamic-import';
import { useDrawerController } from '../shell/useDrawerController';
import { DrawerControlContext } from '../shell/DrawerControlContext';
import { setLoggingEnabled } from '../shared/utils/logger';
import {
  getIsNewCompatiblePage, getIsLegacyCompatiblePage, waitForIdle,
} from '../shared/utils/assistant-utils';
// Renderer-free manifest import (see journeys/registry.js): safe for the eager main
// chunk — it does NOT pull the assistant runtime.
import { resolveJourneyId, DEFAULT_JOURNEY_ID } from '../journeys/registry';
import { useStickyElementOffset } from '../hooks/useStickyElementOffset';
import {
  HEADER_ENTRY, MAGIC_APRON_CONTAINER_ID, UPDATE_DRAWER_STATE,
} from '../shared/assistant-config';
import { buildAnalyticsQuestion } from '../shared/analytics';
import './app.scss';
import { JourneyProvider } from '../journeys';

// App is the eager bootstrap/composition root: it decides whether the assistant runs on
// this surface, which journey is active, and wires the host-event bus — then renders the
// FAB + the deferred assistant tree (the data provider is kept mounted while the drawer
// is closed so queries warm ahead of open, wrapping the lazy Shell rendered once the
// drawer opens). The drawer state machine + FAB behavior live in the Shell's eager
// controller (useDrawerController, WS-10.8); App holds no drawer or session state. Both lazy
// boundaries retry their own chunk import (see createLazyComponent), so no retry wrapper
// lives here.
//
// It also decides which journeyId to resolve: the host-selected one (WS-6), or `legacy` —
// forced regardless of what the host requested — when the unification flags below say this
// surface has not opted into the consolidated experience. `legacy` is a journey like any
// other, registered in `journeyLoaders` (journeys/registry.js) exactly like
// paintColorPicker/journey.jsx; this is not master's live-chat-unification switch
// returning — that one picked between two npm builds of the component and had no journey
// concept at all to route through.

// If the lazy provider subtree throws during render — e.g. the intermittent "invalid
// hook call" when the data-provider chunk resolves against a duplicate React — it must
// be caught by a boundary that is a PARENT of it (a boundary never catches its own
// render errors). Logging it here gives a triage signal instead of a blank assistant
// and keeps the FAB alive. This catches render/commit errors only; an async chunk-load
// failure is surfaced inside createLazyComponent (rethrowOnError) to this same boundary.
const logProviderError = ({ error, name }) => {
  // eslint-disable-next-line no-console
  console.error(`[magic-apron-assistant] "${name}" boundary caught an error:`, error);
};

export const App = ({ activateStyles }) => {
  const { isConsumerApp, customer } = useContext(ExperienceContext);
  const { lesser: isMobile } = useBreakpoint('lg');
  // The active journey the host page selected via the set-journey lifecycle event (WS-6).
  // Threaded down to the shell's JourneyProvider; defaults to the eager core.
  const [journeyId, setJourneyId] = useState(DEFAULT_JOURNEY_ID);
  const offsetMargin = useStickyElementOffset();

  const fsDTEnabled = useConfigService(FLAGS.assistantDtEnabled);
  const fsMWEnabled = useConfigService(FLAGS.assistantMwEnabled);
  const fsAPPEnabled = useConfigService(FLAGS.assistantAppEnabled);
  const fsRenderOnB2B = useConfigService(FLAGS.assistantB2bEnabled) ?? false;
  // Opts INTO the consolidated tree. Legacy is the default, so anything that is not an
  // explicit true — unset, still resolving, or false — means legacy.
  const fsUnifiedEnabled = useConfigService(FLAGS.assistantUnifiedEnabled);
  const fsUnifiedAppEnabled = useConfigService(FLAGS.assistantUnifiedAppEnabled);
  const isValidCustomerType = isCustomerTypeAllowed(customer?.type, fsRenderOnB2B);
  // Page eligibility (MAG4-815, merged from master). Master read this inside `AppSwitch` and
  // gated it on `isLiveChatUnificationEnabled && isExclusionRulesEnabled`, coupling the rules
  // to the tree it chose. Deliberately NOT coupled to `assistantUnifiedEnabled` here, for two
  // reasons: which pages the assistant may run on is loader chrome and package-agnostic, and
  // eligibility is decided at MOUNT while the tree is not settled until later — so a
  // tree-coupled gate would read a flag that has not resolved yet and silently disable the
  // rules. Gated on the rules flag alone; when it is off, the legacy allow-list applies
  // exactly as before, for both trees.
  const isExclusionRulesEnabled = useConfigService(FLAGS.exclusionRulesEnabled);
  const excludedPagesConfig = useConfigService(FLAGS.excludedPages);
  const isCompatiblePage = isExclusionRulesEnabled
    ? getIsNewCompatiblePage(excludedPagesConfig)
    : getIsLegacyCompatiblePage();

  useEffect(() => {
    if (isCompatiblePage && activateStyles) {
      activateStyles();
    }
  }, [isCompatiblePage]);

  const isLoggingEnabled = useConfigService('enable-maa-lc-console-logs');

  useEffect(() => {
    setLoggingEnabled(!!isLoggingEnabled);
  }, [isLoggingEnabled]);

  const {
    drawerState,
    handleOpen,
    handleClose,
    handleDrawerStateChange,
    markShouldAutoOpen,
    openDrawer,
  } = useDrawerController();

  const openDrawerRef = useRef(openDrawer);
  openDrawerRef.current = openDrawer;

  // Has the drawer EVER been open this page-load? A latch, not a mirror of drawerState (Seq
  // G.3): the GraphQL data layer mounts the first time the shopper engages and then stays
  // mounted, so a close-then-reopen never rebuilds the Apollo client or re-warms the queries.
  // Most PDP loads never open the drawer at all, and those pay nothing for the data layer.
  const hasEverOpenedRef = useRef(false);
  if (drawerState !== 'CLOSED') hasEverOpenedRef.current = true;
  const withDataLayer = hasEverOpenedRef.current;

  // Which journey resolves. Legacy is the default — only an explicit `true` selects the
  // consolidated experience, so an unset flag resolves `legacy` rather than nothing.
  //
  // This used to be frozen on the first DEFINED value of either flag and never revisited —
  // a config change was not allowed to swap the tree, because the two trees were rendered as
  // fully separate subtrees (their own ErrorBoundary + DrawerFrame each) and a swap meant
  // unmounting the whole portal/drawer and mounting the other fresh: a visible teardown.
  // That is no longer true. AssistantShell is the ONE shared frame for every journey,
  // including `legacy` (journeys/legacy/journey.js) — a journeyId change now only swaps what
  // AssistantShell.jsx (ShellHeader and its own Body helper) render inside it, the same as
  // any other journey swap (useResolvedJourney keeps serving the previous journey's content
  // until the new one's chunk lands, and the orchestrator stays mounted throughout). There
  // is nothing left for a latch to protect, so `isUnified` is read live on every render.
  const isUnifiedAllowedHere = !isConsumerApp || fsUnifiedAppEnabled;
  const isUnified = Boolean(fsUnifiedEnabled) && isUnifiedAllowedHere;
  // The host page's requested journeyId is honored only once this surface is unified;
  // legacy has no journey concept of its own to route a hint into.
  const activeJourneyId = isUnified ? journeyId : 'legacy';

  // Drawer commands handed DOWN to the assistant subtree, replacing the descendant→ancestor
  // MAA_OPEN_DRAWER broadcast hop. Memoized on `openDrawer`'s behavior rather than identity:
  // the controller rebuilds its handlers every render, so an unmemoized object here would
  // give every consumer a new context value on each App render. `openDrawer` closes over
  // refs and state setters only, so a stable wrapper is safe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const drawerControl = useMemo(() => ({ openDrawer: () => openDrawerRef.current() }), []);

  const handleMagicApronEvents = ({ eventName, output }) => {
    // Entry-point journey selection: the host page fires
    // `magic-apron-assistant.set-journey` with { journeyId } to route the assistant into
    // a specific journey. Normalized against the registry so an unknown/stale hint falls
    // back to the default journey.
    if (eventName === 'set-journey') {
      setJourneyId(resolveJourneyId(output?.journeyId));
    }
    if (eventName === 'question-ask') {
      const {
        messageOrigin,
        pageType,
        entryLocationForAnalytics,
        userQuestion,
        sessionID,
        actionID
      } = output;
      markShouldAutoOpen();
      emit(HEADER_ENTRY.SUBMIT_EVENT, {
        sessionID,
        question: buildAnalyticsQuestion({
          entryLocation: entryLocationForAnalytics,
          pageType,
          messageOrigin,
          userQuestion,
        }),
        actionID,
      });
    }
  };

  useEffect(() => {
    const unsubscribeEvents = subscribe('magic-apron-assistant.*', handleMagicApronEvents);
    return () => {
      unsubscribeEvents();
    };
  }, []);

  // scoped to the unification cohort
  const isHeaderEntryEligible = Boolean(
    isUnified
    && isCompatiblePage
    && isValidCustomerType
    && !isMobile
    && fsDTEnabled
  );

  // Above the `isCompatiblePage` early return on purpose: an excluded page still has to say
  // `available: false`.
  useEffect(() => {
    emit(
      HEADER_ENTRY.STATUS_EVENT,
      { available: isHeaderEntryEligible, drawerState },
      { channel: 'lifeCycle' },
    );
  }, [isHeaderEntryEligible, drawerState]);

  const handleHeaderRequestRef = useRef();
  handleHeaderRequestRef.current = (requestedDrawerState) => {
    if (requestedDrawerState === 'OPEN') handleOpen(true, HEADER_ENTRY.ORIGIN_HEADER);
    if (requestedDrawerState === 'CLOSED') handleClose(HEADER_ENTRY.ORIGIN_HEADER);
  };

  // Requests are absolute, so the bus replaying them — including on StrictMode's second
  // mount — is a no-op rather than a flip.
  useEffect(() => {
    const unsubscribeHeaderEntry = subscribe(
      UPDATE_DRAWER_STATE,
      ({ output } = {}) => handleHeaderRequestRef.current(output?.drawerState),
      { channel: 'lifeCycle' },
    );
    return () => {
      unsubscribeHeaderEntry();
    };
  }, []);

  const isSurfaceReady = isSurfaceEnabled({
    isMobile,
    isConsumerApp,
    dtEnabled: fsDTEnabled,
    mwEnabled: fsMWEnabled,
    appEnabled: fsAPPEnabled,
  // Parenthesize before adding an `&&` term here — it binds tighter than the `||` above.
  }) && isValidCustomerType && isCompatiblePage;

  // Prefetch the Shell chunk ahead of drawer-open. See .claude/context/shell-composition.md.
  useEffect(() => {
    if (!isSurfaceReady) return;
    waitForIdle(loadAssistantShell);
  }, [isSurfaceReady]);

  if (!isSurfaceReady) return null;

  // journeyId here is activeJourneyId — legacy overrides the host page's request when the
  // surface is not unified.
  const drawerProps = {
    drawerState,
    offsetMargin,
    journeyId: activeJourneyId,
    handleClose,
    handleDrawerStateChange,
  };

  return (
    <>
      {(drawerState === 'CLOSED') && (
        <ImpressionProvider
          data={{
            id: MAGIC_APRON_CONTAINER_ID,
            component: 'MagicApronAssistant',
            name: 'MagicApronLoader',
            type: 'content'
          }}
        >
          <AssistantActionButton
            offsetMargin={offsetMargin}
            onClick={() => handleOpen(true)}
            isUnifiedExperience={isUnified}
          />
        </ImpressionProvider>
      )}
      <ErrorBoundary name="magic-apron-assistant-dynamic-provider" onError={logProviderError}>
        {/* The drawer-control context wraps every journey. `legacy` has no consumer for it
            — the consumers are the orchestrator and the Assistant journey — but its default
            is an inert no-op, so leaving it above costs nothing. */}
        <DrawerControlContext.Provider value={drawerControl}>
          <LazyAssistantDataProvider journeyId={activeJourneyId} withDataLayer={withDataLayer}>
            {/* journeyId goes to BOTH halves: the provider needs it above the orchestrator
              to inject the active journey's flows/config (Seq F.c.0), the Shell needs it
              below for JourneyProvider (render-side renderers + content). The provider is
              mounted while CLOSED — that is the warm path. */}
            {(drawerState !== 'CLOSED') && (
              <JourneyProvider journeyId={activeJourneyId}>
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <LazyAssistantShell {...drawerProps} />
              </JourneyProvider>
            )}
          </LazyAssistantDataProvider>
        </DrawerControlContext.Provider>
      </ErrorBoundary>
    </>
  );
};

App.propTypes = {
  activateStyles: PropTypes.func,
};
